import React from 'react';
import { arrayOf, number, func, bool, object } from 'prop-types';
import { routePropTypes } from '../../../../proptypes/shared';
import styles from './styles/styles.module.scss';
import Routes from '../Routes';
import RouteDetails from '../RouteDetails';
import { StringConstantsMixin } from '../../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import Button from '../../../shared/Button';
import { ADD_NEW_ROUTE, defaultRouteColor } from '../../../../hooks/useRoutes';
import { MapStatsUtil } from '../../../../utils/map_stats_util';
import { ServerCommunicationUtil } from '../../../../utils/server_communication_util';
import StaticMapImage from '../../StaticMapImage';

const mapCreatorStrings = StringConstantsMixin.strings.MAP_CREATOR;
const initialColor = '#FF0000';

const RouteSection = ({
  routes,
  context,
  summaryStats,
  dispatchRoutes,
  messagingChannel,
  canEdit,
  sortMode,
  mapId,
  updateMap,
  openTrailFormModal,
  isEditing,
  handleRouteSave,
  isSubmitting,
  updateExploreMap,
  hidePanel,
  ...props
}) => {
  let addNewRoute;
  let handleRouteUpload;
  let openUploadModal;
  let actionButtons;

  if (canEdit) {
    addNewRoute = () => {
      const editIndex = routes.length;
      const route = {
        id: null,
        lineGeoStats: {
          distanceTotal: 0,
          elevationGain: 0
        },
        location: {
          longitude: null,
          latitude: null
        },
        lineDisplayProperty: {
          color: initialColor
        },
        sequence_num: editIndex + 1
      };
      const payload = { route, editIndex };
      const action = { type: ADD_NEW_ROUTE, payload };

      dispatchRoutes(action);
    };

    handleRouteUpload = payload => {
      if (mapId) {
        Object.assign(payload, { lineDisplayProperty: { color: defaultRouteColor } });
        dispatchRoutes({ type: ADD_NEW_ROUTE, payload: { route: payload, editIndex: null } });
      } else {
        updateExploreMap(payload);
      }
    };

    openUploadModal = () => {
      const url = mapId ? `/api/alltrails/maps/${mapId}/routes/gpx` : '/api/alltrails/maps/gpx';
      const createAsMap = mapId === null;

      const formModalProps = { routeMode: true, url, exploreFlag: false, handleRouteUpload, createAsMap };
      openTrailFormModal('tracks', formModalProps);
    };

    actionButtons =
      props.editIndex === null && !sortMode ? (
        <div className={styles.actionButtons}>
          <Button onClick={addNewRoute} variant="secondary">
            {StringConstantsMixin.strings.TRACK_DETAIL.DRAW_ROUTE}
          </Button>
          <Button onClick={openUploadModal} variant="secondary">
            {StringConstantsMixin.strings.CONTRIBUTE.UPLOAD_ROUTE}
          </Button>
        </div>
      ) : null;
  }

  const renderTotalRouteDetails = () => {
    if (!summaryStats || routes.length < 2 || props.editIndex !== null) {
      return;
    }
    const { distance, elevation } = MapStatsUtil.distanceAndElevation(summaryStats, context.displayMetric, context.languageRegionCode);

    return (
      <div className={styles.routeDetailsContainer}>
        <RouteDetails distance={distance} elevation={elevation} />
      </div>
    );
  };

  const totalRouteDetails = renderTotalRouteDetails();

  return (
    <div className={styles.routeContainer}>
      <div className={styles.routeHeaderContainer}>
        {isEditing && <h2 className={styles.routeContainerTitle}>{mapCreatorStrings.CUSTOM_ROUTE}</h2>}
        {props.editIndex !== null && <p className={styles.routeContainerSubtext}>{mapCreatorStrings.SEARCH_FOR_LOCATION}</p>}
      </div>
      {totalRouteDetails}
      {mapId && (
        <div className={styles.staticMapImage}>
          <StaticMapImage url={`/api/alltrails/maps/${mapId}/static_map?size=480x200&key=${ServerCommunicationUtil.apiKey}`} onClick={hidePanel} />
        </div>
      )}
      <Routes
        routes={routes}
        context={context}
        editIndex={props.editIndex}
        sortMode={sortMode}
        dispatchRoutes={dispatchRoutes}
        editParams={props.editParams}
        messagingChannel={messagingChannel}
        handleFindLocationSelect={props.handleFindLocationSelect}
        mapboxAccessToken={props.mapboxAccessToken}
        canEdit={canEdit}
        mapId={mapId}
        updateMap={updateMap}
        handleRouteSave={handleRouteSave}
        isSubmitting={isSubmitting}
      />
      {actionButtons}
    </div>
  );
};

RouteSection.propTypes = {
  routes: arrayOf(routePropTypes),
  dispatchRoutes: func,
  canEdit: bool,
  sortMode: bool,
  mapId: number,
  updateMap: func,
  summaryStats: object,
  openTrailFormModal: func
};

RouteSection.defaultProps = {
  routes: [],
  dispatchRoutes: () => {},
  summaryStats: null,
  canEdit: false,
  sortMode: false,
  mapId: null,
  updateMap: () => {},
  openTrailFormModal: () => {}
};

export default RouteSection;
